/**
 * Project Card Settings Popover
 *
 * Provides quick access to customize project card display settings.
 * Allows users to:
 * - Toggle default collapsed state
 * - Show/hide specific sections (units, financial, parts, planning)
 * - Reset to defaults
 */

import React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Divider,
} from '@heroui/react';
import { Switch } from '@/components/ui/switch';
import { Gear, ArrowClockwise } from '@phosphor-icons/react';
import { useSettings } from 'shared/contexts/settings.context';
import { RenovationCardSettings } from 'shared/types/settings';

interface ProjectCardSettingsPopoverProps {
  className?: string;
}

export const ProjectCardSettingsPopover: React.FC<ProjectCardSettingsPopoverProps> = ({
  className = '',
}) => {
  const { getRenovationCardSettings, updateRenovationCardSettings, isLoading } =
    useSettings();

  const settings = getRenovationCardSettings();

  const handleToggleDefaultCollapsed = async () => {
    await updateRenovationCardSettings({
      defaultCollapsed: !settings.defaultCollapsed,
    });
  };

  const handleTogglePartsExpanded = async () => {
    await updateRenovationCardSettings({
      partsExpanded: !settings.partsExpanded,
    });
  };

  const handleToggleSection = async (section: keyof RenovationCardSettings['visibleSections']) => {
    await updateRenovationCardSettings({
      visibleSections: {
        ...settings.visibleSections,
        [section]: !settings.visibleSections[section],
      },
    });
  };

  const handleToggleWarnings = async () => {
    await updateRenovationCardSettings({
      showWarnings: !settings.showWarnings,
    });
  };

  const handleResetToDefaults = async () => {
    await updateRenovationCardSettings({
      defaultCollapsed: false,
      partsExpanded: false,
      visibleSections: {
        units: true,
        financial: true,
        parts: true,
        planning: true,
      },
      showWarnings: true,
    });
  };

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Button
          isIconOnly
          variant="light"
          className={`bg-neutral-100 hover:bg-neutral-200 ${className}`}
          size="sm"
          isDisabled={isLoading}
        >
          <Gear size={18} weight="fill" className="text-neutral-600" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-neutral-primary">
              Kaart weergave
            </h3>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onClick={handleResetToDefaults}
              className="hover:bg-neutral-100"
              title="Reset naar standaard"
            >
              <ArrowClockwise size={16} weight="bold" className="text-neutral-600" />
            </Button>
          </div>

          <Divider />

          {/* Default Collapsed Setting */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-neutral-primary">
                  Standaard ingeklapt
                </p>
                <p className="text-xs text-neutral-500">
                  Toon kaarten standaard ingeklapt
                </p>
              </div>
              <Switch
                checked={settings.defaultCollapsed}
                onCheckedChange={handleToggleDefaultCollapsed}
                className="data-[state=checked]:bg-moss-primary data-[state=unchecked]:bg-neutral-900"
              />
            </div>
          </div>

          <Divider />

          {/* Parts Expanded Setting */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-neutral-primary">
                  Projectonderdelen uitgeklapt
                </p>
                <p className="text-xs text-neutral-500">
                  Toon onderdelen direct uitgeklapt op alle kaarten
                </p>
              </div>
              <Switch
                checked={settings.partsExpanded}
                onCheckedChange={handleTogglePartsExpanded}
                className="data-[state=checked]:bg-moss-primary data-[state=unchecked]:bg-neutral-900"
              />
            </div>
          </div>

          <Divider />

          {/* Warnings Setting */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-neutral-primary">
                  Waarschuwingen tonen
                </p>
                <p className="text-xs text-neutral-500">
                  Toon waarschuwingen over ontbrekende offertes
                </p>
              </div>
              <Switch
                checked={settings.showWarnings}
                onCheckedChange={handleToggleWarnings}
                className="data-[state=checked]:bg-moss-primary data-[state=unchecked]:bg-neutral-900"
              />
            </div>
          </div>

          <Divider />

          {/* Visible Sections */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Zichtbare secties
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="units-toggle"
                  className="cursor-pointer flex-1 text-sm text-neutral-700"
                >
                  Units
                </label>
                <Switch
                  id="units-toggle"
                  checked={settings.visibleSections.units}
                  onCheckedChange={() => handleToggleSection('units')}
                  className="data-[state=checked]:bg-moss-primary data-[state=unchecked]:bg-neutral-900"
                />
              </div>

              <div className="flex items-center justify-between">
                <label
                  htmlFor="financial-toggle"
                  className="cursor-pointer flex-1 text-sm text-neutral-700"
                >
                  Financieel overzicht
                </label>
                <Switch
                  id="financial-toggle"
                  checked={settings.visibleSections.financial}
                  onCheckedChange={() => handleToggleSection('financial')}
                  className="data-[state=checked]:bg-moss-primary data-[state=unchecked]:bg-neutral-900"
                />
              </div>

              <div className="flex items-center justify-between">
                <label
                  htmlFor="parts-toggle"
                  className="cursor-pointer flex-1 text-sm text-neutral-700"
                >
                  Projectonderdelen
                </label>
                <Switch
                  id="parts-toggle"
                  checked={settings.visibleSections.parts}
                  onCheckedChange={() => handleToggleSection('parts')}
                  className="data-[state=checked]:bg-moss-primary data-[state=unchecked]:bg-neutral-900"
                />
              </div>

              <div className="flex items-center justify-between">
                <label
                  htmlFor="planning-toggle"
                  className="cursor-pointer flex-1 text-sm text-neutral-700"
                >
                  Planning
                </label>
                <Switch
                  id="planning-toggle"
                  checked={settings.visibleSections.planning}
                  onCheckedChange={() => handleToggleSection('planning')}
                  className="data-[state=checked]:bg-moss-primary data-[state=unchecked]:bg-neutral-900"
                />
              </div>
            </div>
          </div>

          <Divider />

          {/* Info Text */}
          <p className="text-xs text-neutral-500">
            Instellingen worden automatisch opgeslagen en toegepast op alle projectkaarten.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};
